import { createSelector } from '@reduxjs/toolkit';
import { Dictionary } from '@reduxjs/toolkit/src/entities/models';
import { contentAdapter, tagAdapter } from './Contents.reducer';
import { Content, ContentTypes } from './Contents.model';

export const ContentsSelectors = {
  contents: {
    ...contentAdapter.getSelectors((state: any) => state.contents),
    selectByIds: createSelector(
      (state: any) => state.contents.contents.entities,
      (_, ids: string[]) => ids,
      (contents, ids) => {
        return Object.values(contents as Dictionary<Content>).filter(
          (content): content is Content =>
            Boolean(content && ids.includes(content.id)),
        );
      },
    ),
    selectArticles: createSelector(
      (state: any) => state.contents.contents.entities,
      (contents) => {
        return Object.fromEntries(
          Object.entries(contents as Dictionary<Content>).filter(
            ([_, v]) =>
              v?.type?.toUpperCase() === ContentTypes.ARTICLE.toUpperCase(),
          ),
        );
      },
    ),
    selectPublishedContentsByCategory: createSelector(
      [
        (state: any) => state.contents.contents.entities,
        (_, sortBy) => sortBy,
        (_, _2, type: string) => type,
      ],
      (contents, sortBy: 'latest' | 'most-viewed' | 'most-liked', type) => {
        return Object.values(contents as Dictionary<Content>)
          .filter((content): content is Content => Boolean(content))
          .filter((content) => {
            if (content.status?.toUpperCase() !== 'PUBLISHED') return false;
            const contentType = content.type?.toUpperCase();

            if (
              type === 'DISCOVER' &&
              contentType === ContentTypes.QUIZ.toUpperCase()
            )
              return false;
            if (type === 'BOOKMARKS' && !content.bookmarked) return false;
            if (
              type === 'QUIZZES' &&
              contentType !== ContentTypes.QUIZ.toUpperCase()
            )
              return false;

            return true;
          })
          .sort((a, b) => {
            if (sortBy === 'latest')
              return (
                new Date(b.createdAt as string).getTime() -
                new Date(a.createdAt as string).getTime()
              );
            if (sortBy === 'most-viewed')
              return (
                (b.interaction?.numberOfViews ?? 0) -
                (a.interaction?.numberOfViews ?? 0)
              );
            if (sortBy === 'most-liked')
              return (
                (b.interaction?.numberOfLikes ?? 0) -
                (a.interaction?.numberOfLikes ?? 0)
              );
            return 0;
          })
          .reduce((acc: Dictionary<Content[]>, cur) => {
            const { category } = cur;
            if (!category) return acc;
            acc[category] = acc[category] ?? [];
            acc[category]?.push(cur);
            return acc;
          }, {} as Dictionary<Content[]>);
      },
    ),
  },
  categories: {
    ...tagAdapter.getSelectors((state: any) => state.categories),
  },
  tags: {
    ...tagAdapter.getSelectors((state: any) => state.tags),
  },
};
